import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const listenersRef = useRef(new Set());

  const [challengePopup, setChallengePopup] = useState(null);

  const addListener = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => listenersRef.current.delete(callback);
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const backendUrl = 'https://admin.krazybets.online';
    const wsUrl = backendUrl.replace(/^http/, 'ws') + '/api/ws';
    const token = document.cookie.split(';').find(c => c.trim().startsWith('access_token='))?.split('=')[1];
    const fullUrl = token ? `${wsUrl}?token=${token}` : wsUrl;

    try {
      const ws = new WebSocket(fullUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') return;
          
          setLastEvent(data);
          listenersRef.current.forEach(cb => cb(data));

          // Show toast notifications for key events
          if (data.type === 'your_bet_matched') {
            setChallengePopup({ type: 'bet', matcher: data.matcher, bet_id: data.bet_id });
          } else if (data.type === 'bet_matched') {
            toast.success(`Bet matched by ${data.matcher}!`);
          } else if (data.type === 'your_bet_approved') {
            toast.success('Your bet has been approved and is now live!');
          } else if (data.type === 'your_rps_accepted') {
            setChallengePopup({ type: 'rps', opponent: data.opponent, challenge_id: data.challenge_id });
          } else if (data.type === 'rps_round_result') {
            const result = data.round_winner === 'tie' ? 'Tie!' : `Round winner: ${data.round_winner}`;
            toast.info(`RPS: ${result} (You: ${data.your_move}, Them: ${data.their_move})`);
          } else if (data.type === 'rps_completed') {
            toast.success(`RPS game completed! Winner: ${data.winner}`);
          }
        } catch (e) {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;
        // Reconnect after 5 seconds
        reconnectTimerRef.current = setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      // WebSocket not available
    }
  }, []);

  useEffect(() => {
    connect();

    // Ping every 30s to keep alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect, isAuthenticated]);

  return (
    <WebSocketContext.Provider value={{ isConnected, lastEvent, addListener, challengePopup, setChallengePopup }}>
      {children}
      {/* Big Challenge Accepted Popup */}
      {challengePopup && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4" data-testid="challenge-popup">
          <div className="bg-[#1A1A1A] border-2 border-[#D4AF37] rounded-2xl p-8 max-w-sm w-full text-center animate-bounce-in">
            <div className="text-6xl mb-4">{challengePopup.type === 'rps' ? '\u2694\ufe0f' : '\ud83c\udfaf'}</div>
            <h2 className="text-2xl font-black text-[#D4AF37] mb-2" style={{ fontFamily: 'Outfit' }}>
              {challengePopup.type === 'rps' ? 'CHALLENGER ACCEPTED!' : 'BET MATCHED!'}
            </h2>
            <p className="text-white text-lg mb-2">
              <span className="text-[#28A745] font-bold">{challengePopup.matcher || challengePopup.opponent}</span>
            </p>
            <p className="text-gray-400 mb-6">
              {challengePopup.type === 'rps' ? 'has accepted your RPS challenge! Click to enter the duel arena.' : 'has matched your bet!'}
            </p>
            <button
              onClick={() => {
                if (challengePopup.type === 'rps' && challengePopup.challenge_id) {
                  window.location.href = `/rps/duel/${challengePopup.challenge_id}`;
                } else if (challengePopup.bet_id) {
                  window.location.href = `/bet/${challengePopup.bet_id}`;
                }
                setChallengePopup(null);
              }}
              className="bg-[#D4AF37] text-[#1A1A1A] font-black text-lg px-8 py-3 rounded-xl hover:bg-[#C4A037] transition-colors w-full mb-3"
              data-testid="popup-play-btn"
            >
              {challengePopup.type === 'rps' ? 'CLICK TO PLAY' : 'VIEW BET'}
            </button>
            <button onClick={() => setChallengePopup(null)} className="text-gray-500 text-sm hover:text-white">
              Dismiss
            </button>
          </div>
        </div>
      )}
    </WebSocketContext.Provider>
  );
};
